/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, August 3rd 2022, 4:50:21 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, May 6th 2022, 11:32:35 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useGlobalScope } from "core/providers";
import { APISERVICEPATHS, useAPIFetchItem, useAPIForm } from "lib/api";
import {
  DatasetSchema,
  DatasetSchemaValidationSchema,
  DatasetImportInput,
  DatasetImportInputValidationSchema,
} from "lib/schemas";
import { getDefaultDatasetSchema } from "lib/factories";
import { APISearchParameters } from "lib/types";

const datasetSchemasAPIServicePath = APISERVICEPATHS.datasetSchemas;
const datasetSchemaImportAPIServicePath = APISERVICEPATHS.datasetSchemaImport;
const datasetSchemaExportAPIServicePath = APISERVICEPATHS.datasetSchemaExport;

interface UseDatasetSchemaAPIFormArgs {
  dataBankId: DatasetSchema["data_bank"];
  datasetSchemaId?: DatasetSchema["id"];
}

const useDatasetSchemaAPIForm = ({ dataBankId, datasetSchemaId = null }: UseDatasetSchemaAPIFormArgs) => {
  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();

  const [datasetSchema, setDatasetSchema] = React.useState<DatasetSchema>(
    getDefaultDatasetSchema(selectedOrganization.id, dataBankId)
  );

  const useAPIFormReturn = useAPIForm<DatasetSchema>({
    apiFormDefaultValues: datasetSchema,
    apiFormDataValidationSchema: DatasetSchemaValidationSchema,
    setAPIFormResponse: setDatasetSchema,
    apiInitialFetchId: datasetSchemaId,
    apiServicePath: datasetSchemasAPIServicePath,
    primaryKey: "id",
    successNotificationMessage: `Successfully updated the ${datasetSchema.json_schema?.title} dataset`,
    formResetDependencyArray: [datasetSchema?.id],
  });

  return { useAPIFormReturn };
};

const useImportDataAPIForm = (datasetSchemaId: DatasetSchema["id"]) =>
  useAPIForm<DatasetImportInput>({
    apiFormDefaultValues: { csv_file: null },
    apiFormDataValidationSchema: DatasetImportInputValidationSchema,
    setAPIFormResponse: () => {},
    apiInitialFetchId: null,
    apiServicePath: datasetSchemaImportAPIServicePath,
    apiPathParams: [datasetSchemaId],
    primaryKey: null,
    successNotificationMessage: `Successfully import data into dataset`,
  });

export const useDatasetSchemaAPI = () => ({
  useDatasetSchemaAPIForm,
  useImportDataAPIForm,
  useExportDataAPI: () => {
    const getDownloadURL = (datasetSchemaId: DatasetSchema["id"], type: "csv" | "json") => {
      return `${process.env.REACT_APP_ONESTOP_API_BASE_URL}${datasetSchemaExportAPIServicePath}${datasetSchemaId}/?file_type=${type}`;
    };

    return { getDownloadURL };
  },
  useAPIFetchDatasetSchema: (filters?: APISearchParameters<DatasetSchema>) =>
    useAPIFetchItem<DatasetSchema>({
      apiServicePath: datasetSchemasAPIServicePath,
      apiQueryParams: { ...filters },
      makeRequestOnInitialMount: false,
    }),
});
